import { Component, Input, Output, EventEmitter, HostBinding, ViewChild, ElementRef, AfterViewInit } from '@angular/core';

@Component({
  selector: 'form-field',
  template: `<fieldset class="form-group" [ngClass]="{'required':required, 'has-error': showError}">
        <label class="form-control-label" *ngIf="label">{{label}}</label>
        <input type="text"
          [ngModel]="model"
          #field
          (input)="onInput(field)"
          (blur)="onBlur(field)"
          (invalid)="onInvalid(field)"
          [attr.required]="required || null"
          [attr.pattern]="pattern || null"
          [attr.data-x-v]="dataXV || null"
          [attr.maxlength]="maxLength || null"
          [attr.minlength]="minLength || null"
          [ngClass]="{'is-invalid': showError}"
          autocomplete="new-password"
          class="form-control"
          placeholder="{{placeholder||label}}">
        <div class="invalid-feedback" *ngIf="showError && errorMessage" [ngStyle]="{'display': showError ? 'block' : 'none'}">
          {{errorMessage}}
        </div>
      </fieldset>`
})
export class FieldCmp implements AfterViewInit {
  @Input() label = '';
  @Input() placeholder: any = false;
  @Input() model;
  @Input() required;
  @Input() pattern: string;
  @Input() mask: string;
  @Input() dataXV: string; // data-x-v attribute
  @Input() errorMessages: any = {};
  @Input() maxLength: number;
  @Input() minLength: number;
  @Output('modelChange') update = new EventEmitter();

  @ViewChild('field', { static: false }) inputElement: ElementRef;

  showError = false;
  errorMessage = '';

  ngAfterViewInit() {
    // Apply mask if provided
    if (this.mask && this.inputElement) {
      this.setupMask();
    }
  }

  updateModel(v) {
    this.update.emit(v);
  }

  onInput(input: HTMLInputElement) {
    // Apply mask
    if (this.mask) {
      this.applyMask(input);
    }

    // Validate and show errors
    this.validateField(input);
    this.updateModel(input.value);
  }

  onBlur(input: HTMLInputElement) {
    this.validateField(input);
  }

  onInvalid(input: HTMLInputElement) {
    this.validateField(input);
  }

  validateField(input: HTMLInputElement) {
    this.showError = false;
    this.errorMessage = '';

    const value = input.value.trim();

    // Check required
    if (this.required && !value) {
      this.showError = true;
      this.errorMessage = this.errorMessages.required || 'This field is required.';
      input.setCustomValidity(this.errorMessage);
      return;
    }

    // Check length constraints
    if (this.minLength && value.length < this.minLength) {
      this.showError = true;
      this.errorMessage = this.errorMessages.minlength || `Minimum length is ${this.minLength} characters.`;
      input.setCustomValidity(this.errorMessage);
      return;
    }

    if (this.maxLength && value.length > this.maxLength) {
      this.showError = true;
      this.errorMessage = this.errorMessages.maxlength || `Maximum length is ${this.maxLength} characters.`;
      input.setCustomValidity(this.errorMessage);
      return;
    }

    // Check pattern
    if (this.pattern && value) {
      const regex = new RegExp(this.pattern);
      if (!regex.test(value)) {
        this.showError = true;
        this.errorMessage = this.errorMessages.pattern || 'Invalid format.';
        input.setCustomValidity(this.errorMessage);
        return;
      }
    }

    // Clear custom validity if all checks pass
    input.setCustomValidity('');
  }

  setupMask() {
    // Setup mask based on mask format
    if (!this.inputElement) return;
  }

  applyMask(input: HTMLInputElement) {
    if (!this.mask) return;

    const value = input.value.replace(/\D/g, ''); // Remove non-digits

    if (this.mask === '999-999-9999') {
      // Phone mask
      if (value.length <= 3) {
        input.value = value;
      } else if (value.length <= 6) {
        input.value = value.slice(0, 3) + '-' + value.slice(3);
      } else {
        input.value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
      }
    } else if (this.mask === '99999' || this.mask === '99999-9999') {
      // Zip mask
      if (value.length <= 5) {
        input.value = value;
      } else {
        input.value = value.slice(0, 5) + '-' + value.slice(5, 9);
      }
    }
  }
}
